import { getBackendUrl } from "@/utils/api";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { usePageContent } from "@/hooks/usePageContent";
import { useSEO } from "@/hooks/useSEO";
import { CheckCircle, Users, BarChart3, Shield, MessageSquare, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = getBackendUrl();

export default function JoinUsPage() {
  const { get, loading: contentLoading } = usePageContent("joinus");
  const [seoData, setSeoData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    agency_name: "",
    contact_name: "",
    email: "",
    phone: "",
    website: "",
    message: ""
  });

  useEffect(() => {
    fetchSeoData();
  }, []);

  const fetchSeoData = async () => {
    try {
      const response = await axios.get(`${API}/api/seo/joinus`);
      setSeoData(response.data);
    } catch (error) {
      console.error("Error fetching SEO data:", error);
    }
  };

  // Apply SEO
  useSEO(
    seoData ? {
      seo_title: seoData.seo_title || "Become a Partner | SellLocalHomes",
      seo_description: seoData.seo_description || "Join our network of trusted estate agents.",
      seo_keywords: seoData.seo_keywords || "estate agent, partner, property listing",
      seo_robots: seoData.seo_robots || "index, follow",
      canonical_url: "https://selllocalhomes.co.uk/join-us"
    } : {
      seo_title: "Become a Partner | SellLocalHomes",
      seo_description: "Join our network of trusted estate agents.",
      seo_keywords: "estate agent, partner, property listing",
      seo_robots: "index, follow",
      canonical_url: "https://selllocalhomes.co.uk/join-us"
    },
    { og_type: "website" }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.agency_name || !formData.contact_name || !formData.email || !formData.phone) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/api/agent-applications`, formData);
      if (response.data.status === "success") {
        setSubmitted(true);
        toast.success("Application submitted successfully!");
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to submit application";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: Users,
      title: "Wider Reach",
      description: get("benefit_1", "Reach a wider audience of qualified buyers")
    },
    {
      icon: BarChart3,
      title: "Professional Listings",
      description: get("benefit_2", "Professional property listings with high-quality images")
    },
    {
      icon: Shield,
      title: "Easy Management",
      description: get("benefit_3", "Easy-to-use property management dashboard")
    },
    {
      icon: MessageSquare,
      title: "Direct Enquiries",
      description: get("benefit_4", "Direct enquiries from interested buyers")
    }
  ];

  if (submitted) {
    return (
      <PageShell>
        <div className="min-h-screen" data-testid="joinus-page">
        
        <main className="pt-12 md:pt-16 pb-24">
          <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
            <div className="bg-primary/5 rounded-lg p-12 border border-primary/10">
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-6" />
              <h1 className="font-serif text-3xl md:text-4xl mb-4">Application Submitted!</h1>
              <p className="text-muted-foreground text-lg mb-6">
                Thank you for your interest in becoming a SellLocalHomes partner. 
                We've received your application and will review it shortly.
              </p>
              <p className="text-muted-foreground mb-8">
                You will receive a confirmation email with further details. 
                Our team will get back to you within 2-3 business days.
              </p>
              <Link to="/">
                <Button className="bg-primary text-white hover:bg-primary/90 rounded-none">
                  Return to Homepage
                </Button>
              </Link>
            </div>
          </div>
        </main>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="min-h-screen" data-testid="joinus-page">
      
      
      {/* Hero Section */}
      <section className="pt-12 md:pt-16 pb-16 bg-gradient-to-b from-background to-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-primary tracking-[0.3em] uppercase text-sm font-medium">
              {get("page_subtitle", "Partner With Us")}
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-4 mb-6">
              {get("page_title", "Become a Partner")}
            </h1>
            <p className="text-muted-foreground text-lg md:text-xl">
              {get("page_description", "Join our network of trusted estate agents and reach thousands of potential buyers.")}
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="font-serif text-2xl md:text-3xl text-center mb-12">
            {get("benefits_title", "Why Partner With Us?")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="bg-background p-6 rounded-lg border border-border hover:border-primary/30 transition-colors"
              >
                <benefit.icon className="h-10 w-10 text-primary mb-4" />
                <h3 className="font-serif text-lg mb-2">{benefit.title}</h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="bg-background border border-border rounded-lg p-8 md:p-12">
            <h2 className="font-serif text-2xl md:text-3xl mb-2 text-center">
              {get("form_title", "Create Your Account")}
            </h2>
            <p className="text-muted-foreground text-center mb-8">
              {get("form_description", "Fill in your details below and we'll get you set up in no time.")}
            </p>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="partner-application-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="agency_name">
                    {get("agency_name_label", "Agency Name")} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="agency_name"
                    name="agency_name"
                    value={formData.agency_name}
                    onChange={handleChange}
                    placeholder="Your Agency Name"
                    required
                    data-testid="agency-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_name">
                    {get("contact_name_label", "Contact Name")} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="contact_name"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    placeholder="Your Full Name"
                    required
                    data-testid="contact-name-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    {get("email_label", "Email Address")} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@agency.com"
                    required
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">
                    {get("phone_label", "Phone Number")} <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+44 7xxx xxx xxx"
                    required
                    data-testid="phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">
                  {get("website_label", "Website (Optional)")}
                </Label>
                <Input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://www.youragency.com"
                  data-testid="website-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">
                  {get("message_label", "Tell us about your agency")}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your agency, how many properties you manage, and what you're looking for in a partnership..."
                  rows={4}
                  data-testid="message-input"
                />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-primary text-white hover:bg-primary/90 rounded-none h-12"
                  data-testid="submit-application-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    get("submit_btn", "Submit Application")
                  )}
                </Button>
                <Link to="/contact" className="flex-1">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full rounded-none h-12"
                    data-testid="enquire-cta-btn"
                  >
                    {get("enquire_cta", "Contact Us First")}
                  </Button>
                </Link>
              </div>

              <p className="text-xs text-muted-foreground text-center mt-4">
                By submitting this form, you agree to our{" "}
                <Link to="/terms" className="text-primary hover:underline">
                  Terms and Conditions
                </Link>
              </p>
            </form>
          </div>
        </div>
      </section>
      </div>
    </PageShell>
  );
}
